import React from 'react';
import {
  ADD_UNIT,
  EDIT_UNIT,
  ARCHIVE_UNIT,
  UNARCHIVE_UNIT,
  LOAD_UNITS,
  LOAD_UNIT,
  LOAD_ARCHIVE_UNITS,
  PROCESS_UNITS,
  PROCESS_UNIT,
  PROCESS_EDIT_UNIT,
  PROCESS_NEW_UNIT,
  insertUnit,
  removeUnit,
  PROCESS_ARCHIVE_UNIT,
  PROCESS_UNARCHIVE_UNIT,
  UNIT_ERROR,
  BULK_ASSOCIATE_PARENT_UNIT,
  UNIT_EDIT_ERROR,
  UNIT_ARCHIVE_ERROR,
  UNITS_LOAD_ERROR,
  loadProcessedUnits,
  loadUnits,
  UNITS_IMPORT_ERROR,
  CONFIRM_IMPORT_UNITS,
  IMPORT_UNITS,
  PROCESS_EXPORT_UNITS,
  EXPORT_UNITS,
  setLastUnitsChecked,
} from '../actions/unitActions';
import { apiRequest } from '../actions/apiActions';
import {
  showLoading,
  hideLoading,
  addUiMsg,
  updateApiSpinner,
} from '../actions/uiActions';
import { apiUrls, apiVersion } from '../../apiUrls';
import { FormattedMessage } from 'react-intl';
import {
  unitText,
  newUnitText,
  wasNotCreatedText,
  wasNotUpdatedText,
  wasNotArchivedText,
  wasCreatedText,
  wasUpdatedText,
  wasArchivedText,
  wasUnArchivedText,
} from '../../components/shared/SharedInternationalizations';
import { messageTypes } from '../../components/ui/AlertMessage';
import { addAlarmDeviceDetails } from '../actions/alarmDeviceActions';
import { addAlertDeviceDetails } from '../actions/alertDeviceActions';
import { errorDisplay, redirect } from '../../utilities/general';
import { getUser } from '../actions/userActions';

/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const loadAllUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_UNITS) {
    const currentTime = new Date();
    const lastChecked = new Date(store.getState().units.lastChecked);
    let timeFilter = '';
    if (currentTime > lastChecked) {
      timeFilter = '&modified_date_gte=' + lastChecked.toISOString();
    }

    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        '?is_active=True' +
        timeFilter,
      onSuccess: PROCESS_UNITS,
      onError: UNITS_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };

    store.dispatch(showLoading());
    store.dispatch(setLastUnitsChecked(currentTime));
    store.dispatch(updateApiSpinner({ unitsReturned: false }));
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const loadUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_UNIT) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        action.payload +
        '/',
      onSuccess: PROCESS_UNIT,
      onError: UNITS_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to trigger an api call to retrieve all of the units
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to retrieve the units
 */
export const loadArchiveUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === LOAD_ARCHIVE_UNITS) {
    const body = null;
    const meta = {
      method: 'GET',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        '?is_active=False',
      onSuccess: PROCESS_UNITS,
      onError: UNITS_LOAD_ERROR,
      auth: store.getState().auth.token,
      body: false,
    };
    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the returned units array from the api request.
 * @description - This takes the array, sorts them by parent unit, then checks to see if the unit already exists in the existing units object. If it does, it checks if the modified date of the returned unit is newer than the existing unit and if the returned unit is newer, it will replace the current unit, otherwise it will retain the current one. If the unit doesn't exist already, it will create it and add it to the object
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#loadProcessedUnits - Dispatches the load processed units action to load the returned units into the Redux store
 */
export const processUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_UNITS) {
    if (action.payload.next) {
      const body = null;
      const meta = {
        method: 'GET',
        url: action.payload.next,
        onSuccess: PROCESS_UNITS,
        onError: UNITS_LOAD_ERROR,
        auth: store.getState().auth.token,
        body: false,
      };
      store.dispatch(apiRequest(body, meta));
    } else {
      store.dispatch(hideLoading());
    }

    const units = action.payload.results;

    if (units.length === 0) {
      store.dispatch(updateApiSpinner({ unitsReturned: true }));
    }
    let processedUnits = store.getState().units.units;
    units.map((unit) => {
      const incomingModifiedDate = new Date(unit.modified_date);
      let previousModifiedDate;

      // This checks to see if the returned unit already exists in the units object
      if (processedUnits[unit.id]) {
        previousModifiedDate = new Date(processedUnits[unit.id].modified_date);
      }
      // If the unit doesn't exist, it will be created and added to the object
      if (!processedUnits[unit.id]) {
        processedUnits[unit.id] = { ...unit };

        // If the unit does exist, the modified date is checked to see if it's newer from the existing unit
      } else if (previousModifiedDate < incomingModifiedDate) {
        // If the returned unit is newer, it will replace the existing one in the units object
        processedUnits[unit.id] = { ...unit };
      }
      return '';
    });
    const currentTime = new Date();
    store.dispatch(loadProcessedUnits(processedUnits, currentTime));
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const unitLoadError = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNITS_LOAD_ERROR) {
    store.dispatch(hideLoading());

    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="unitMiddleware.unitsNotLoadedText"
          defaultMessage="Units not loaded"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );
    const errorText = (
      <React.Fragment>
        <FormattedMessage
          id="unitMiddleware.unitsNotLoadedErrorText"
          defaultMessage="The units were not loaded. If you are correctly logged in, try refreshing the page to reload"
          description="The units were not able to be loaded"
        />
      </React.Fragment>
    );

    const message = {
      message: messageText,
      error: errorText,
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};
/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const unitError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === UNIT_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {newUnitText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.name}"
        </span>
        {wasNotCreatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};
/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const unitEditError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === UNIT_EDIT_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {newUnitText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.name}"
        </span>
        {wasNotUpdatedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
  }
};
/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const unitArchiveError = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === UNIT_ARCHIVE_ERROR) {
    dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        {unitText}
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.originalPayload.name}"
        </span>
        {wasNotArchivedText}
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };
    dispatch(addUiMsg(message));
    console.error(action);
  }
};

/**
 * Middleware to trigger an api call to add a new unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the unit
 */
export const addUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === ADD_UNIT) {
    const unitInfo = action.payload;
    let parent_unit;
    if (unitInfo.hasParentUnit) {
      parent_unit = action.payload.parentUnitId;
    }

    const nextSteps = [];

    if (unitInfo.usesCheckin) {
      nextSteps.push('createCheckInPreferences');
    }

    const prevSteps = ['unitCreate'];

    const formSteps = {
      type: 'units',
      nextSteps,
      prevSteps,
      id: action.payload.id,
    };

    const body = {
      id: action.payload.id,
      name: action.payload.name,
      address1: action.payload.address1,
      address2: action.payload.address2,
      city: action.payload.city,
      state: action.payload.state,
      zipcode: action.payload.zipcode,
      is_active: action.payload.is_active,
      parent_unit: parent_unit,
    };

    const meta = {
      method: 'POST',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].unit,
      onSuccess: PROCESS_NEW_UNIT,
      onError: UNIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: formSteps,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to add the newly created unit to the Redux store
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers adding the created unit to the units object in Redux
 * @fires dispatch#reset - Resets the add new unit form
 */
export const processNewUnit = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_NEW_UNIT) {
    dispatch(hideLoading());
    dispatch(insertUnit(action.payload));
    const info = action;
    info.meta.formSteps.id = info.payload.id;
    info.meta.previous = '/units';
    let url = '/units/?path=' + info.payload.path;
    redirect(info, url);

    const messageText = (
      <React.Fragment>
        {newUnitText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.name}"
        </span>
        {wasCreatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to trigger an api call to edit a preexisting unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to edit the unit
 */
export const editUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === EDIT_UNIT) {
    const finalForm = { ...action.payload };
    const unit = store.getState().units.units[finalForm.id];

    if (!finalForm.name && unit.name) {
      finalForm.name = '';
    }
    if (!finalForm.address1 && unit.address1) {
      finalForm.address1 = '';
    }
    if (!finalForm.address2 && unit.address2) {
      finalForm.address2 = '';
    }
    if (!finalForm.city && unit.city) {
      finalForm.city = '';
    }
    if (!finalForm.state && unit.state) {
      finalForm.state = '';
    }
    if (!finalForm.zipcode && unit.zipcode) {
      finalForm.zipcode = '';
    }
    if (unit.parent_unit) {
      if (!finalForm.parentUnitId) {
        finalForm.parent_unit = '';
      } else if (!finalForm.hasParentUnit) {
        finalForm.parent_unit = '';
      }
    } else if (finalForm.parentUnitId) {
      finalForm.parent_unit = finalForm.parentUnitId;
    }

    const nextSteps = [];

    if (finalForm.usesCheckin) {
      nextSteps.push('createCheckInPreferences');
    }

    const prevSteps = ['unitCreate'];

    const formSteps = {
      type: 'units',
      nextSteps,
      prevSteps,
      id: finalForm.id,
    };

    const body = {
      id: finalForm.id,
      name: finalForm.name,
      address1: finalForm.address1,
      address2: finalForm.address2,
      city: finalForm.city,
      state: finalForm.state,
      zipcode: finalForm.zipcode,
      is_active: finalForm.is_active,
      parent_unit: finalForm.parent_unit,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        action.payload.id +
        '/',
      onSuccess: PROCESS_EDIT_UNIT,
      onError: UNIT_EDIT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      formSteps: formSteps,
      previous: action.previous,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers replacing the current unit information with the updated unit information
 * @fires history.push - Tells react-router to switch back to the normal view of the unit from the edit view
 */
export const processUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_UNIT) {
    store.dispatch(hideLoading());
    store.dispatch(insertUnit(action.payload));
    const alarmArray = action.payload.alarm_devices;
    const alarmDetails = {};
    alarmArray.map((device) => {
      alarmDetails[device.alarm_device_id] = {
        alarm_device_id: device.alarm_device_id,
        alarm_description: device.alarm_description,
        unit: device.unit,
        user: device.user,
        alarm_device_type: device.alarm_device_type,
        hardware_type: device.hardware_type,
      };
      return '';
    });
    store.dispatch(addAlarmDeviceDetails(alarmDetails));
    const alertArray = action.payload.alert_devices;
    const alertDetails = {};
    alertArray.map((device) => {
      alertDetails[device.alert_device_id] = {
        alert_device_id: device.alert_device_id,
        alert_device_type: device.alert_device_type,
        alert_group_id: device.alert_group_id,
        unit: device.unit,
        user: device.user,
        email_address: device.email_address,
        phone_number: device.phone_number,
        interface_name: device.interface_name,
        alert_description: device.alert_description,
        notes: device.notes,
      };
      return '';
    });
    store.dispatch(addAlertDeviceDetails(alertDetails));
  }
};

/**
 * Middleware to process the updated unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUnit - Triggers replacing the current unit information with the updated unit information
 * @fires navigate - Tells react-router to switch back to the normal view of the unit from the edit view
 */
export const processEditUnit = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_EDIT_UNIT) {
    dispatch(hideLoading());
    dispatch(insertUnit(action.payload));

    let url = '/units/' + action.payload.id + '/view/';
    // let url = "/users/view/" + action.payload.id + "/";
    redirect(action, url);

    const messageText = (
      <React.Fragment>
        {unitText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.name}"
        </span>
        {wasUpdatedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
    dispatch(loadUnits(message));
  }
};

/**
 * Middleware to archive a unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to archive the unit
 */
export const archiveUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === ARCHIVE_UNIT) {
    const usersArr = Object.values(store.getState().users.users);
    const assignedUsers = usersArr.filter((user) => {
      if (user.units.includes(action.payload.id)) {
        return true;
      } else {
        return false;
      }
    });

    const body = {
      name: action.payload.name,
      is_active: false,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        action.payload.id +
        '/',
      onSuccess: PROCESS_ARCHIVE_UNIT,
      onError: UNIT_ARCHIVE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
      assignedUsers: assignedUsers,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to unarchive a unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to archive the unit
 */
export const unArchiveUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNARCHIVE_UNIT) {
    const body = {
      is_active: true,
    };

    const meta = {
      method: 'PATCH',
      url:
        apiUrls.baseUrl +
        apiVersion +
        apiUrls[apiVersion].unit +
        action.payload.id +
        '/',
      onSuccess: PROCESS_UNARCHIVE_UNIT,
      onError: UNIT_ARCHIVE_ERROR,
      auth: store.getState().auth.token,
      body: true,
      navigate: action.navigate,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the archived unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#removeUnit - Triggers removing the unit from the redux store
 * @fires navigate - Tells react-router to switch back to the units list view
 */
export const processArchiveUnit = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_ARCHIVE_UNIT) {
    dispatch(hideLoading());
    const navigate = action.meta.navigate; // This is the navigate function passed from React Router v6

    // by passing this through the actions the interface can be redirected in the middleware
    // without having to use an integration between react-router and redux.
    const url = '/units/';
    if (navigate) {
      navigate(url); // This sends the user back to the base url for the user they were editing
    }
    dispatch(removeUnit(action.payload));
    if (action.meta.assignedUsers.length > 0) {
      action.meta.assignedUsers.map((u) => {// eslint-disable-line
        dispatch(getUser(u.id));
      });
    }
    const messageText = (
      <React.Fragment>
        {unitText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.name}"
        </span>
        {wasArchivedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to process the unarchived unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#removeUnit - Triggers removing the unit from the redux store
 * @fires navigate - Tells react-router to switch back to the units list view
 */
export const processUnArchiveUnit = ({ dispatch }) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_UNARCHIVE_UNIT) {
    dispatch(hideLoading());
    const navigate = action.meta.navigate; // This is the navigate function passed from React Router v6
    const path = action.payload.path;

    // by passing this through the actions the interface can be redirected in the middleware
    // without having to use an integration between react-router and redux.
    const url = '/units/?path=' + path;
    if (navigate) {
      navigate(url); // This sends the user back to the base url for the user they were editing
    }
    dispatch(insertUnit(action.payload));
    const messageText = (
      <React.Fragment>
        {unitText}:
        <span className="text-italic margin-left-5 margin-right-5">
          "{action.payload.name}"
        </span>
        {wasUnArchivedText}
      </React.Fragment>
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    dispatch(addUiMsg(message));
  }
};

/**
 * Middleware to archive a unit
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to archive the unit
 */
export const bulkAssociateParentUnit = (store) => (next) => (action) => {
  next(action);
  if (action.type === BULK_ASSOCIATE_PARENT_UNIT) {
    const unit = action.payload.unit;
    const unitIds = action.payload.unitIds;
    const initialUnits = action.payload.initialUnits;

    unitIds.map((id) => {
      if (!initialUnits.includes(id)) {
        const body = {
          parent_unit: unit,
        };

        const meta = {
          method: 'PATCH',
          url:
            apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].unit + id + '/',
          onSuccess: PROCESS_EDIT_UNIT,
          onError: UNIT_EDIT_ERROR,
          auth: store.getState().auth.token,
          body: true,
          navigate: action.navigate,
          previous: action.previous,
        };

        store.dispatch(showLoading());
        store.dispatch(apiRequest(body, meta));
      }
      return '';
    });

    initialUnits.map((id) => {
      if (!unitIds.includes(id)) {
        const body = {
          parent_unit: '',
        };

        const meta = {
          method: 'PATCH',
          url:
            apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].unit + id + '/',
          onSuccess: PROCESS_EDIT_UNIT,
          onError: UNIT_ERROR,
          auth: store.getState().auth.token,
          body: true,
          navigate: action.navigate,
          previous: action.previous,
        };

        store.dispatch(showLoading());
        store.dispatch(apiRequest(body, meta));
      }
      return '';
    });
  }
};

/**
 * Middleware to trigger an api call to add a new user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the user
 */
export const importUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === IMPORT_UNITS) {
    const body = action.payload[0];
    const meta = {
      method: 'PUT',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].importUnits,
      onSuccess: CONFIRM_IMPORT_UNITS,
      onError: UNITS_IMPORT_ERROR,
      auth: store.getState().auth.token,
      body: true,
      preferences: action.payload,
      navigate: action.navigate,
      previous: action.previous,
      csv: true,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};

/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires navigate - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const confirmImportUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === CONFIRM_IMPORT_UNITS) {
    store.dispatch(hideLoading());
    if (action.meta.navigate) {
      const navigate = action.meta.navigate; // This is the navigate function passed from React Router v6
      // by passing this through the actions the interface can be redirected in the middleware
      // without having to use an integration between react-router and redux.

      let url = '/units/';
      if (action.meta.previous) {
        url = action.meta.previous;
      }

      navigate(url); // This sends the user back to the base url for the user they were editing
    }
    const messageText = (
      <FormattedMessage
        id="unitMiddleware.unitsImportConfirmText"
        defaultMessage="Units have been imported."
        description="Opens editing something"
      />
    );
    const message = {
      message: messageText,
      messageType: messageTypes.success,
    };

    store.dispatch(addUiMsg(message));
    store.dispatch(loadUnits());
  }
};

/**
 * Middleware to handle when there is an error with the api
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 */
export const importUnitsError = (store) => (next) => (action) => {
  next(action);
  if (action.type === UNITS_IMPORT_ERROR) {
    store.dispatch(hideLoading());
    const messageText = (
      <React.Fragment>
        <FormattedMessage
          id="unitMiddleware.importUnitsError"
          defaultMessage="There was a problem importing your units"
          description="The user statuses were not able to be loaded"
        />
      </React.Fragment>
    );
    const errors = action.payload;
    const message = {
      message: messageText,
      error: errorDisplay(errors),
      messageType: messageTypes.danger,
    };

    store.dispatch(addUiMsg(message));
    console.error(action);
  }
};

/**
 * Middleware to trigger an api call to add a new user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#showLoading - Triggers the showing of the loader bar
 * @fires dispatch#apiRequest - Starts api request to create the user
 */
export const exportUsers = (store) => (next) => (action) => {
  next(action);
  if (action.type === EXPORT_UNITS) {
    const body = '';
    const meta = {
      method: 'GET',
      url: apiUrls.baseUrl + apiVersion + apiUrls[apiVersion].exportUnits,
      onSuccess: PROCESS_EXPORT_UNITS,
      onError: UNIT_ERROR,
      auth: store.getState().auth.token,
      body: false,
      preferences: action.payload,
      navigate: action.navigate,
      previous: action.previous,
      payload: action.payload,
    };

    store.dispatch(showLoading());
    store.dispatch(apiRequest(body, meta));
  }
};
/**
 * Middleware to process the updated user
 * @param {object} action - The action triggered by the action creator
 * @fires dispatch#hideLoading - Triggers the hiding of the loader bar
 * @fires dispatch#insertUser - Triggers replacing the current user information with the updated user information
 * @fires navigate - Tells react-router to switch back to the normal view of the user from the edit view
 * @fires dispatch#userMsgEditSuccess - Triggers showing the user edit success message
 * @fires dispatch#clearUserMsg - Triggers hiding of the user message
 */
export const processExportUnits = (store) => (next) => (action) => {
  next(action);
  if (action.type === PROCESS_EXPORT_UNITS) {
    store.dispatch(hideLoading());
    if (action.meta.navigate) {
      let url = '/units/';
      redirect(action, url);
    }
    action.meta.payload(action.payload);
  }
};

export const unitMdl = [
  loadAllUnits,
  loadArchiveUnits,
  processUnits,
  unitError,
  processUnit,
  addUnit,
  loadUnit,
  processNewUnit,
  archiveUnit,
  unArchiveUnit,
  processArchiveUnit,
  processUnArchiveUnit,
  editUnit,
  bulkAssociateParentUnit,
  unitEditError,
  unitArchiveError,
  unitLoadError,
  processEditUnit,
  importUnits,
  confirmImportUnits,
  importUnitsError,
  exportUsers,
  processExportUnits,
];
